import { useState, useEffect } from 'react';
import { api, fmt } from '../api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import DateField from '../components/DateField';

const COLORS = ['#CC7A3D', '#8FA85E', '#D4A24C', '#C4553D', '#8B5FA8', '#6B7F47'];

function StatBox({ label, value, sub, color }) {
  return (
    <div className="stat-card">
      <div className="label">{label}</div>
      <div className="value" style={{color: color || 'var(--text)', fontSize: 22}}>{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

function TopProductsTable({ products }) {
  if (!products?.length) return <div className="empty-state" style={{padding:24}}><div>No data</div></div>;
  return (
    <table style={{width:'100%'}}>
      <thead><tr><th>#</th><th>Product</th><th>Qty Sold</th><th>Revenue</th></tr></thead>
      <tbody>
        {products.map((p, i) => (
          <tr key={p.product}>
            <td className="mono text-muted">{i + 1}</td>
            <td style={{fontWeight:600}}>{p.product}</td>
            <td className="mono">{p.qty}</td>
            <td className="mono text-green">{fmt.currency(p.revenue)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ReportView({ data }) {
  if (!data) return null;

  const pieData = data.topProducts?.map(p => ({ name: p.product, value: p.revenue })) || [];

  return (
    <div>
      <div className="stat-grid" style={{marginBottom:20}}>
        <StatBox label="Total Revenue" value={fmt.currency(data.totalRevenue)} color="var(--accent2)" />
        <StatBox label="Gross Profit" value={fmt.currency(data.grossProfit)} sub={`${data.grossMargin}% margin`} color="var(--accent)" />
        <StatBox label="Cost of Goods" value={fmt.currency(data.totalCOGS)} color="var(--warn)" />
        <StatBox label="Total Purchases" value={fmt.currency(data.totalPurchases)} sub={`${data.purchaseCount} orders`} color="var(--muted)" />
        <StatBox label="Sales Count" value={data.saleCount} sub="transactions" />
      </div>

      <div className="report-grid">
        <div className="card">
          <div className="section-title">Top Products</div>
          <TopProductsTable products={data.topProducts} />
        </div>

        {pieData.length > 0 && (
          <div className="card">
            <div className="section-title">Revenue by Product</div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({name, percent}) => `${name} (${(percent*100).toFixed(0)}%)`}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt.currency(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="card mb-4">
        <div className="section-title">Sales in Period — {data.saleCount} transactions</div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Date/Time</th><th>Items</th><th>Amount</th><th>Note</th></tr></thead>
            <tbody>
              {data.sales?.length === 0
                ? <tr><td colSpan={4} className="text-muted" style={{textAlign:'center',padding:20}}>No sales in this period</td></tr>
                : data.sales?.map(s => {
                  const total = s.items?.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0) || 0;
                  return (
                    <tr key={s.id}>
                      <td className="mono">{fmt.datetime(s.date)}</td>
                      <td><span className="badge badge-blue">{s.items?.length}</span></td>
                      <td className="mono text-green">{fmt.currency(total)}</td>
                      <td className="text-muted">{s.note || '—'}</td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="section-title">Purchases in Period — {data.purchaseCount} orders</div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Date/Time</th><th>Supplier</th><th>Items</th><th>Cost</th></tr></thead>
            <tbody>
              {data.purchases?.length === 0
                ? <tr><td colSpan={4} className="text-muted" style={{textAlign:'center',padding:20}}>No purchases in this period</td></tr>
                : data.purchases?.map(p => {
                  const total = p.items?.reduce((sum, i) => sum + i.quantity * i.unitCost, 0) || 0;
                  return (
                    <tr key={p.id}>
                      <td className="mono">{fmt.datetime(p.date)}</td>
                      <td>{p.supplier?.name || '—'}</td>
                      <td><span className="badge badge-blue">{p.items?.length}</span></td>
                      <td className="mono text-warn">{fmt.currency(total)}</td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Reports() {
  const [tab, setTab] = useState('daily');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Filters
  const today = new Date().toISOString().slice(0, 10);
  const [dailyDate, setDailyDate] = useState(today);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  const load = async () => {
    setLoading(true);
    setData(null);
    try {
      let result;
      if (tab === 'daily') result = await api.getDailyReport(dailyDate);
      else if (tab === 'monthly') result = await api.getMonthlyReport(year, month);
      else result = await api.getYearlyReport(year);
      setData(result);
    } catch { }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab]);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  return (
    <div>
      <div className="page-header">
        <h2>Reports</h2>
        <p>Financial summaries and analytics</p>
      </div>

      <div className="report-tabs mb-4">
        {['daily','monthly','yearly'].map(t => (
          <button key={t} className={`tab-btn ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div className="card mb-4">
        <div className="flex gap-2" style={{flexWrap:'wrap', alignItems:'flex-end'}}>
          {tab === 'daily' && (
            <div className="field" style={{marginBottom:0}}>
              <label>Date</label>
              <DateField value={dailyDate} onChange={e => setDailyDate(e.target.value)} />
            </div>
          )}
          {(tab === 'monthly' || tab === 'yearly') && (
            <div className="field" style={{marginBottom:0}}>
              <label>Year</label>
              <select value={year} onChange={e => setYear(Number(e.target.value))}>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}
          {tab === 'monthly' && (
            <div className="field" style={{marginBottom:0}}>
              <label>Month</label>
              <select value={month} onChange={e => setMonth(Number(e.target.value))}>
                {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
          )}
          <button className="btn btn-primary" onClick={load} disabled={loading}>
            {loading ? <span className="spinner" /> : '⟳ Generate Report'}
          </button>
        </div>
      </div>

      {loading && <div className="empty-state"><div className="spinner" /></div>}
      {!loading && data && <ReportView data={data} />}
    </div>
  );
}
